import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, LayoutGrid, List, Download, ChevronLeft, ChevronRight } from "lucide-react";
import {
  listOnboardings,
  createOnboarding,
  exportJoiningReport,
  formatLifecycleError,
} from "./lifecycleApi";
import { getEmployees } from "../Employees/EmployeeApi";
import { downloadBlob } from "../Employees/employeeFileUtils";
import { getNextEmployeeCode } from "../Employees/EmployeeApi";

const ONBOARDING_STATUSES = [
  "Draft",
  "Pending Approval",
  "Approved",
  "In Progress",
  "Completed",
  "Employee Activated",
];

const ONBOARDING_STATUS_COLORS = {
  Draft: "lc-badge lc-badge--slate",
  "Pending Approval": "lc-badge lc-badge--amber",
  Approved: "lc-badge lc-badge--blue",
  "In Progress": "lc-badge lc-badge--violet",
  Completed: "lc-badge lc-badge--blue",
  "Employee Activated": "lc-badge lc-badge--blue",
};

const EMPLOYEE_STATUS_COLORS = {
  active: "lc-badge lc-badge--blue",
  inactive: "lc-badge lc-badge--slate",
  on_leave: "lc-badge lc-badge--amber",
  terminated: "lc-badge lc-badge--rose",
};

const PAGE_SIZE = 15;

function empDisplayName(emp) {
  return [emp.first_name, emp.middle_name, emp.last_name].filter(Boolean).join(" ") || "—";
}

function formatStatusLabel(status) {
  if (!status) return "—";
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildOnboardingMaps(items = []) {
  const byEmployeeId = new Map();
  const byEmail = new Map();
  for (const ob of items) {
    if (ob.employee_id) byEmployeeId.set(String(ob.employee_id), ob);
    const email = (ob.email || "").trim().toLowerCase();
    if (email && !byEmail.has(email)) byEmail.set(email, ob);
  }
  return { byEmployeeId, byEmail };
}

async function fetchAllOnboardings() {
  const all = [];
  let page = 1;
  let total = 0;
  do {
    const res = await listOnboardings({ page, page_size: 100 });
    all.push(...(res.items || []));
    total = res.total ?? all.length;
    page += 1;
  } while (all.length < total && page <= 50);
  return all;
}

function resolveOnboarding(emp, maps) {
  const byId = maps.byEmployeeId.get(String(emp.employee_id));
  if (byId) return byId;
  const email = (emp.work_email || emp.email || "").trim().toLowerCase();
  if (email) return maps.byEmail.get(email) || null;
  return null;
}

export default function LcOnboarding() {
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [onboardingMaps, setOnboardingMaps] = useState({ byEmployeeId: new Map(), byEmail: new Map() });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [employeeStatusFilter, setEmployeeStatusFilter] = useState("");
  const [onboardingFilter, setOnboardingFilter] = useState("");
  const [page, setPage] = useState(1);
  const [view, setView] = useState("list");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ employee_name: "", email: "", employee_type: "Permanent", employee_code: "" });
  const [codeLoading, setCodeLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, employeeStatusFilter, onboardingFilter]);

  useEffect(() => {
    if (!showCreate) return;
    setCodeLoading(true);
    getNextEmployeeCode()
      .then(({ employee_code }) => {
        if (employee_code) setForm((f) => ({ ...f, employee_code }));
      })
      .finally(() => setCodeLoading(false));
  }, [showCreate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const activeOnly =
        employeeStatusFilter === "active"
          ? true
          : employeeStatusFilter === "inactive"
            ? false
            : undefined;

      const useClientPaging = Boolean(onboardingFilter);
      const fetchPage = useClientPaging ? 1 : page;
      const fetchSize = useClientPaging ? 200 : PAGE_SIZE;

      const [empRes, onboardingItems] = await Promise.all([
        getEmployees({
          page: fetchPage,
          page_size: fetchSize,
          search: debouncedSearch || undefined,
          active_only: activeOnly,
        }),
        fetchAllOnboardings(),
      ]);

      setEmployees(Array.isArray(empRes?.items) ? empRes.items : []);
      setTotal(typeof empRes?.total === "number" ? empRes.total : 0);
      setOnboardingMaps(buildOnboardingMaps(onboardingItems));
    } catch (e) {
      setError(formatLifecycleError(e, "Failed to load employees"));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, employeeStatusFilter, onboardingFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  const allRows = useMemo(() => {
    return employees.map((emp) => {
      const onboarding = resolveOnboarding(emp, onboardingMaps);
      const tasks = onboarding?.tasks || [];
      const completedTasks = tasks.filter((t) => t.status === "Completed").length;
      return { emp, onboarding, completedTasks, totalTasks: tasks.length };
    }).filter(({ onboarding }) => {
      if (!onboardingFilter) return true;
      if (onboardingFilter === "none") return !onboarding;
      if (onboardingFilter === "active") return !!onboarding;
      return onboarding?.status === onboardingFilter;
    });
  }, [employees, onboardingMaps, onboardingFilter]);

  const rows = useMemo(() => {
    if (onboardingFilter) {
      const start = (page - 1) * PAGE_SIZE;
      return allRows.slice(start, start + PAGE_SIZE);
    }
    return allRows;
  }, [allRows, onboardingFilter, page]);

  const displayTotal = onboardingFilter ? allRows.length : total;
  const totalPages = Math.max(1, Math.ceil(displayTotal / PAGE_SIZE));

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const created = await createOnboarding(form);
      setShowCreate(false);
      setForm({ employee_name: "", email: "", employee_type: "Permanent", employee_code: "" });
      window.location.href = `/employee-lifecycle/onboarding/${created.onboarding_id}`;
    } catch (err) {
      setError(formatLifecycleError(err, "Create failed"));
    }
  };

  const handleExport = async () => {
    const blob = await exportJoiningReport();
    downloadBlob(blob, "joining_report.csv");
  };

  const kanbanGroups = useMemo(() => {
    const groups = [
      { key: "none", label: "No onboarding", items: [] },
      ...ONBOARDING_STATUSES.map((s) => ({ key: s, label: s, items: [] })),
    ];
    const bucket = new Map(groups.map((g) => [g.key, g]));
    for (const row of rows) {
      const key = row.onboarding?.status || "none";
      const group = bucket.get(key) || bucket.get("none");
      group.items.push(row);
    }
    return groups.filter((g) => g.items.length > 0 || g.key === "none");
  }, [rows]);

  return (
    <div className="lc-onboarding">
      <div className="lc-toolbar">
        <div>
          <h2 className="lc-toolbar-title">Employees</h2>
          <p className="lc-toolbar-sub">{displayTotal} in organisation · onboarding &amp; lifecycle</p>
        </div>
        <div className="lc-toolbar-actions">
          <button type="button" onClick={handleExport} className="lc-btn-ghost">
            <Download size={15} /> Export
          </button>
          <div className="lc-view-toggle">
            <button
              type="button"
              onClick={() => setView("list")}
              className={view === "list" ? "is-active" : ""}
              aria-label="List view"
            >
              <List size={15} />
            </button>
            <button
              type="button"
              onClick={() => setView("kanban")}
              className={view === "kanban" ? "is-active" : ""}
              aria-label="Board view"
            >
              <LayoutGrid size={15} />
            </button>
          </div>
          <button type="button" onClick={() => setShowCreate(true)} className="lc-btn-primary">
            <Plus size={15} /> New Onboarding
          </button>
        </div>
      </div>

      <div className="lc-filters">
        <div className="lc-search-wrap">
          <Search size={15} className="lc-search-icon" />
          <input
            className="lc-search-input"
            placeholder="Search name, code, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="lc-select"
          value={employeeStatusFilter}
          onChange={(e) => setEmployeeStatusFilter(e.target.value)}
        >
          <option value="">All employees</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <select
          className="lc-select"
          value={onboardingFilter}
          onChange={(e) => setOnboardingFilter(e.target.value)}
        >
          <option value="">All onboarding states</option>
          <option value="none">No onboarding</option>
          <option value="active">Has onboarding</option>
          {ONBOARDING_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {error && <p className="lc-error">{error}</p>}

      {showCreate && (
        <form onSubmit={handleCreate} className="lc-create-form">
          <h3 className="lc-create-title">New Onboarding</h3>
          <div className="lc-create-grid">
            <input
              readOnly
              className="lc-input lc-input--code"
              placeholder={codeLoading ? "Generating code…" : "Employee code"}
              value={codeLoading ? "" : form.employee_code || ""}
            />
            <input
              required
              className="lc-input"
              placeholder="Employee name"
              value={form.employee_name}
              onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
            />
            <input
              required
              type="email"
              className="lc-input"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <select
              className="lc-input"
              value={form.employee_type}
              onChange={(e) => setForm({ ...form, employee_type: e.target.value })}
            >
              {["Permanent", "Contract", "Probation", "Intern", "Consultant"].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="lc-create-actions">
            <button type="submit" className="lc-btn-primary">Create</button>
            <button type="button" onClick={() => setShowCreate(false)} className="lc-btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="lc-muted">Loading employees…</p>
      ) : view === "kanban" ? (
        <div className="lc-kanban">
          {kanbanGroups.map(({ key, label, items: col }) => (
            <div key={key} className="lc-kanban-col">
              <div className="lc-kanban-col-head">
                {label} <span className="lc-kanban-count">{col.length}</span>
              </div>
              <div className="lc-kanban-cards">
                {col.map(({ emp, onboarding }) => (
                  <Link
                    key={emp.employee_id}
                    to={
                      onboarding
                        ? `/employee-lifecycle/onboarding/${onboarding.onboarding_id}`
                        : `/employees/${emp.employee_id}/edit`
                    }
                    className="lc-kanban-card"
                  >
                    <div className="lc-kanban-card-name">{empDisplayName(emp)}</div>
                    <div className="lc-kanban-card-meta">{emp.employee_code || "—"}</div>
                    <div className="lc-kanban-card-meta">{emp.work_email || emp.email || "—"}</div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="lc-table-wrap">
            <table className="lc-data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>DOJ</th>
                  <th>Employee</th>
                  <th>Onboarding</th>
                  <th>Tasks</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ emp, onboarding, completedTasks, totalTasks }) => (
                  <tr key={emp.employee_id}>
                    <td className="lc-mono">{emp.employee_code || "—"}</td>
                    <td>
                      <span className="lc-emp-name">{empDisplayName(emp)}</span>
                    </td>
                    <td className="lc-muted">{emp.work_email || emp.email || "—"}</td>
                    <td>{emp.date_of_joining || onboarding?.date_of_joining || "—"}</td>
                    <td>
                      <span className={EMPLOYEE_STATUS_COLORS[emp.status] || "lc-badge lc-badge--slate"}>
                        {formatStatusLabel(emp.status)}
                      </span>
                    </td>
                    <td>
                      {onboarding ? (
                        <span className={ONBOARDING_STATUS_COLORS[onboarding.status] || "lc-badge lc-badge--slate"}>
                          {onboarding.status}
                        </span>
                      ) : (
                        <span className="lc-badge lc-badge--slate">Not started</span>
                      )}
                    </td>
                    <td className="lc-muted tabular-nums">
                      {onboarding ? `${completedTasks}/${totalTasks}` : "—"}
                    </td>
                    <td className="lc-actions">
                      {onboarding ? (
                        <Link
                          to={`/employee-lifecycle/onboarding/${onboarding.onboarding_id}`}
                          className="lc-link"
                        >
                          Onboarding
                        </Link>
                      ) : (
                        <Link
                          to={`/employees/${emp.employee_id}/edit`}
                          className="lc-link"
                        >
                          Profile
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={8} className="lc-empty">No employees match your filters</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="lc-pagination">
              <button
                type="button"
                className="lc-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <span className="lc-page-info">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="lc-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
